import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
	Languages,
	Shop,
	salesChartMode,
	OrderProduct,
	RemoteData,
	DateFormatObject,
} from 'common/types';
import { ReduxState } from 'services/types';
import Container from 'components/Container';
import Typography from '@mui/material/Typography';
import moment from 'moment-timezone';
import {
	Theme,
	IconButton,
	Button,
	ButtonGroup,
	Grid,
	Table,
	TableHead,
	TableBody,
	TableCell,
	TableRow,
	Box,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { RiArrowRightSLine, RiArrowLeftSLine, RiArrowLeftRightLine } from 'react-icons/ri';
import classNames from 'classnames';
import { groupProductsBy } from 'common/utils/productUtils';
import {
	addAllOutgoingListener,
	addExpiredIncomingListener,
	addExpiringIncomingListener,
	addDashboardListeners,
} from 'actions';
import { getTranslation } from 'common/modules/translations';
import DashboardChart from 'components/DashboardChart';
import * as aggregator from '../services/aggregator';

import SetPageTitle from 'components/Portals/SetPageTitle';
import useDebouncedValue from 'common/hooks/useDebouncedValue';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { localFormatString } from 'common/utils/dateUtils';
import { useTranslation } from 'services/localization/useTranslation';
import ContainerBox from 'components/ContainerBox';

interface StateToProps {
	lang: Languages;
	shop: Shop;
	allOutgoingProducts: RemoteData<OrderProduct[]>;
	expiringIncomingProducts: RemoteData<OrderProduct[]>;
	expiredIncomingProducts: RemoteData<OrderProduct[]>;
}

interface DispatchProps {
	addAllOutgoingListener: (
		shopId: string,
		startDate: moment.Moment,
		endDate: moment.Moment,
	) => void;
	addExpiredIncomingListener: (
		shopId: string,
		startDate: moment.Moment,
		endDate: moment.Moment,
	) => void;
	addExpiringIncomingListener: (
		shopId: string,
		startDate: moment.Moment,
		endDate: moment.Moment,
	) => void;
}

type Props = DispatchProps & StateToProps & WithStyles<typeof styles>;

enum ViewModeTypes {
	Day = 'day',
	Week = 'week',
	Month = 'month',
	Year = 'year',
}

const viewModeTypesToAdd: Record<ViewModeTypes, moment.DurationInputArg2> = {
	[ViewModeTypes.Day]: 'days',
	[ViewModeTypes.Week]: 'weeks',
	[ViewModeTypes.Month]: 'months',
	[ViewModeTypes.Year]: 'years',
};

const viewModeTypesToISODay: Record<ViewModeTypes, moment.unitOfTime.StartOf> = {
	[ViewModeTypes.Day]: 'days',
	[ViewModeTypes.Week]: 'isoWeek',
	[ViewModeTypes.Month]: 'months',
	[ViewModeTypes.Year]: 'years',
};

const formatDayFromViewMode = (
	day: moment.Moment,
	endDay: moment.Moment,
	viewMode: ViewModeTypes,
	dateFormat: DateFormatObject,
) => {
	const { Day, Week, Month, Year } = ViewModeTypes;
	switch (viewMode) {
		case Day:
			return day.format('ddd MMM Do');
		case Week:
			const dateFormatString = localFormatString('ddd D.M', dateFormat);
			return day.format(dateFormatString) + ' - ' + endDay!.format(dateFormatString);
		case Month:
			return day.format('MMMM');
		case Year:
			return day.format('Y');
		default:
			return day.format('ddd MMM Do');
	}
};

const Dashboard = (props: Props) => {
	const dispatch = useDispatch();
	const { Day, Week, Month, Year } = ViewModeTypes;

	const {
		classes,
		allOutgoingProducts,
		expiringIncomingProducts,
		expiredIncomingProducts,
		shop: {
			publicInfo: { shopId },
		},
	} = props;
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const [currentDay, setCurrentDay] = useState(moment().startOf('day'));
	const debounceCurrentDay = useDebouncedValue(currentDay, 600);
	const [endDay, setEndDay] = useState(currentDay);
	const [viewMode, setViewMode] = useState<ViewModeTypes>(Day);
	const { t } = useTranslation();
	const outgoingProducts =
		allOutgoingProducts.kind === 'FETCHED'
			? !activeLocationId
				? []
				: allOutgoingProducts.data.filter((product) => product.startLocationId === activeLocationId)
			: [];
	const allIncomingProducts =
		expiringIncomingProducts.kind === 'FETCHED' && expiredIncomingProducts.kind === 'FETCHED'
			? expiringIncomingProducts.data.concat(expiredIncomingProducts.data)
			: [];
	const incomingProducts = !activeLocationId
		? []
		: allIncomingProducts.filter(
				(product) => !product.cancelled && product.startLocationId === activeLocationId,
		  );
	const [isLoading, setIsLoading] = useState(true);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);

	useEffect(() => {
		setIsLoading(true);
		dispatch(addDashboardListeners(shopId, debounceCurrentDay, endDay));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debounceCurrentDay]);

	useEffect(() => {
		setIsLoading(
			allOutgoingProducts.kind === 'LOADING' ||
				expiredIncomingProducts.kind === 'LOADING' ||
				expiringIncomingProducts.kind === 'LOADING',
		);
	}, [allOutgoingProducts, expiredIncomingProducts, expiringIncomingProducts]);

	const updateDay = (direction: 'forward' | 'backward') => {
		const numDirection = direction === 'forward' ? 1 : -1;
		const updateWhat = viewModeTypesToISODay[viewMode];
		const addWhat = viewModeTypesToAdd[viewMode];
		const updatedCurrentDay = moment(currentDay).add(numDirection, addWhat);
		const updatedEndDate = moment(updatedCurrentDay).endOf(updateWhat);
		setCurrentDay(updatedCurrentDay);
		setEndDay(updatedEndDate);
	};

	const updateViewMode = (viewType: ViewModeTypes) => {
		setViewMode(viewType);
		const momentViewType = viewType === Week ? viewModeTypesToISODay[viewType] : viewType;
		const updatedCurrentDay = moment(currentDay).startOf(momentViewType);
		const updatedEndDate = moment(currentDay).endOf(momentViewType);
		setCurrentDay(updatedCurrentDay);
		setEndDay(updatedEndDate);
	};

	const renderDaySelector = () => {
		return (
			<>
				{currentDay.startOf(Day).toISOString() === moment().startOf(Day).toISOString() ? (
					<Typography align={'center'} color={'textSecondary'}>
						{t('common:times.today')}
					</Typography>
				) : currentDay.startOf(Day).toISOString() ===
				  moment().add(1, viewModeTypesToAdd[Day]).startOf(Day).toISOString() ? (
					<Typography align={'center'} color={'textSecondary'}>
						{t('common:times.tomorrow')}
					</Typography>
				) : null}
				<div className={classes.monthSelector}>
					<IconButton
						className={classes.button}
						aria-label={t('reports.sales.previousMonth')}
						onClick={() => {
							updateDay('backward');
						}}
						disabled={isLoading}
						size="large"
					>
						<RiArrowLeftSLine />
					</IconButton>
					<Typography className={classes.smallPadding} variant="h4" color={'textPrimary'}>
						{formatDayFromViewMode(currentDay, endDay, viewMode, shopDateFormat)}
					</Typography>
					<IconButton
						className={classes.button}
						aria-label={t('reports.sales.nextMonth')}
						onClick={() => {
							updateDay('forward');
						}}
						disabled={isLoading}
						size="large"
					>
						<RiArrowRightSLine />
					</IconButton>
				</div>
			</>
		);
	};

	const renderViewChange = () => {
		return (
			<Grid container alignItems={'center'} justifyContent={'center'}>
				<Grid item>
					<ButtonGroup
						classes={{ root: classes.btnContainer }}
						size={'small'}
						aria-label="small outlined button group"
					>
						<Button
							variant={'outlined'}
							onClick={() => {
								updateViewMode(Day);
							}}
							className={classNames(classes.Btn, viewMode === Day ? classes.activeBtn : undefined)}
							disabled={isLoading}
						>
							{t('common:times.day')}
						</Button>
						<Button
							variant={'outlined'}
							onClick={() => {
								updateViewMode(Week);
							}}
							className={classNames(classes.Btn, viewMode === Week ? classes.activeBtn : undefined)}
							disabled={isLoading}
						>
							{t('common:times.week')}
						</Button>
						<Button
							variant={'outlined'}
							onClick={() => {
								updateViewMode(Month);
							}}
							className={classNames(
								classes.Btn,
								viewMode === Month ? classes.activeBtn : undefined,
							)}
							disabled={isLoading}
						>
							{t('common:times.month', 'month')}
						</Button>
						<Button
							variant={'outlined'}
							disabled
							onClick={() => {
								updateViewMode(Year);
							}}
							className={classNames(classes.Btn, viewMode === Year ? classes.activeBtn : undefined)}
						>
							{t('common:times.year', 'year')}
						</Button>
					</ButtonGroup>
				</Grid>
			</Grid>
		);
	};

	const renderChart = () => {
		const rentalOutData = aggregator.rentalDataFromOrders(
			outgoingProducts,
			viewMode,
			currentDay,
			'out',
		);
		const rentalInData = aggregator.rentalDataFromOrders(
			incomingProducts,
			viewMode,
			currentDay,
			'in',
		);
		const chartData = aggregator.chartDataFromRentals(
			rentalOutData,
			rentalInData,
			viewMode as salesChartMode,
			currentDay,
		);
		const dataStatusObject = {
			loading: isLoading,
		};
		return (
			<ContainerBox
				className={{
					root: classes.cardRoot,
				}}
			>
				{renderViewChange()}
				<DashboardChart
					data={chartData}
					dataStatus={dataStatusObject}
					year={currentDay.year()}
					month={currentDay.format('MMM')}
					day={currentDay}
					viewMode={Day}
				/>
			</ContainerBox>
		);
	};

	const renderOutgoingRentals = () => {
		return (
			<ContainerBox
				className={{
					root: classes.cardRoot,
				}}
			>
				<Typography variant={'body2'} align={'center'} color={'textSecondary'}>
					{t('dashboard.outgoing', 'Leaving')}
				</Typography>
				<Typography variant={'h3'} align={'center'} className={classes.bigNumber}>
					{isLoading ? '' : outgoingProducts.length}
				</Typography>
			</ContainerBox>
		);
	};

	const renderIncomingRentals = () => {
		return (
			<ContainerBox
				className={{
					root: classes.cardRoot,
				}}
			>
				<Typography variant={'body2'} align={'center'} color={'textSecondary'}>
					{t('dashboard.incoming', 'Returning')}
				</Typography>
				<Typography variant={'h3'} align={'center'} className={classes.bigNumber}>
					{isLoading ? '' : incomingProducts.length}
				</Typography>
			</ContainerBox>
		);
	};

	const renderShoppers = () => {
		const allShoppersWithDuplicate = outgoingProducts.map((product) => product.shopperId);
		const shoppersOutgoing = allShoppersWithDuplicate.filter(
			(shopper, index) => allShoppersWithDuplicate.indexOf(shopper) === index,
		);
		return (
			<ContainerBox
				className={{
					root: classes.cardRoot,
				}}
			>
				<Typography variant={'body2'} align={'center'} color={'textSecondary'}>
					{t('dashboard.customers', 'Customers')}
				</Typography>
				<Typography variant={'h3'} align={'center'} className={classes.bigNumber}>
					{isLoading ? '' : shoppersOutgoing.length}
				</Typography>
			</ContainerBox>
		);
	};

	const renderTableRow = (table: string) => {
		const productsToRender = table === 'outgoing' ? outgoingProducts : incomingProducts;
		const groupedByProducts = groupProductsBy(productsToRender, (product) => product.productApiId);
		return Object.values(groupedByProducts).map((groupedProducts, index) => {
			if (groupedProducts) {
				let productName: string = '';
				productName = groupedProducts[0] && getTranslation(groupedProducts[0].name, props.lang);
				return (
					<TableRow key={index} className={classes.productRow}>
						<TableCell className={classes.borderBottom}>
							<Typography>{productName}</Typography>
						</TableCell>
						<TableCell className={classNames(classes.borderBottom, classes.right)}>
							<Typography>{groupedProducts.length}</Typography>
						</TableCell>
					</TableRow>
				);
			}
			return null;
		});
	};

	const renderTable = (table: string) => {
		const count = table === 'outgoing' ? outgoingProducts.length : incomingProducts.length;
		const tableName =
			table === 'outgoing'
				? t('dashboard.outgoing', 'Leaving')
				: t('dashboard.incoming', 'Returning');
		return (
			<>
				<div>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>
									<Typography>{tableName}</Typography>
								</TableCell>
								<TableCell>
									<Typography align={'right'}>{count}</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>{renderTableRow(table)}</TableBody>
					</Table>
				</div>
			</>
		);
	};

	const renderRentals = () => {
		return (
			<ContainerBox className={classes.rentalsCard}>
				<Box mb={3} className={classes.justifyCenter}>
					<RiArrowLeftRightLine className={classes.rentalsIcon} />
					<Typography variant={'h5'}>{t('headerBar.storeRentals')}</Typography>
				</Box>
				{isLoading ? null : (
					<Grid container spacing={9} alignContent="flex-start">
						<Grid item xs={12} md={6}>
							{renderTable('outgoing')}
						</Grid>
						<Grid item xs={12} md={6}>
							{renderTable('incoming')}
						</Grid>
					</Grid>
				)}
			</ContainerBox>
		);
	};

	return (
		<>
			<SetPageTitle priority={1} title={t('dashboard.dashboard', 'Dashboard')} />
			<Container>
				{renderDaySelector()}
				{renderChart()}

				<Grid container spacing={2} alignContent="flex-start" className={classes.cardContainer}>
					<Grid item xs={12} md={4}>
						{renderOutgoingRentals()}
					</Grid>
					<Grid item xs={12} md={4}>
						{renderIncomingRentals()}
					</Grid>
					<Grid item xs={12} md={4}>
						{renderShoppers()}
					</Grid>
				</Grid>
				{renderRentals()}
			</Container>
		</>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		rootContainer: {
			flex: '1 1 auto',
		},
		monthSelector: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'center',
			margin: '0 auto',
			width: '100%',
		},
		button: {
			margin: '5px',
		},
		mediumHeight: {
			height: '32px',
		},
		lowHeight: {
			height: '24px',
		},
		smallPadding: {
			padding: '12px 0',
		},
		metricContainer: {
			margin: '8px',
			[theme.breakpoints.up('sm')]: {
				width: '45%',
			},
		},
		justifyCenter: {
			display: 'flex',
			alignItems: 'center',
		},
		rentalsIcon: {
			marginRight: theme.spacing(1),
			color: 'inherit',
		},
		btnContainer: {
			borderRadius: 3,
			border: `1px solid ${theme.palette.primary.main}`,
			margin: '0 auto',
			marginBottom: theme.spacing(3),
		},
		right: {
			float: 'right',
		},
		Btn: {
			fontSize: 12,
			fontWeight: 'normal',
			border: '0 !important',
			textTransform: 'capitalize',
			borderRadius: 0,
			minHeight: 0,
			padding: '0 16px',
		},
		activeBtn: {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText,
			borderRadius: '0',
			'&:hover': {
				backgroundColor: theme.palette.primary.dark,
			},
		},
		viewChangeRoot: {
			width: '100%',
		},
		cardContainer: {
			marginTop: theme.spacing(3),
		},
		cardRoot: {
			padding: theme.spacing(5),
		},
		rentalsCard: {
			marginTop: theme.spacing(3),
			padding: `${theme.spacing(3)} ${theme.spacing(3)}`,
		},
		bigNumber: {
			fontSize: theme.spacing(9),
		},
		productRow: {
			maxHeight: theme.spacing(6),
		},
		borderBottom: {
			borderBottom: `1px solid ${theme.palette.border.table}`,
		},
	});

const mapStateToProps = ({ lang, shop, storeRentals }: ReduxState): StateToProps => {
	const activeShop = shop.shop.data!;
	const { allOutgoingProducts, expiredIncomingProducts, expiringIncomingProducts } = storeRentals;
	return {
		lang,
		shop: activeShop,
		allOutgoingProducts,
		expiredIncomingProducts,
		expiringIncomingProducts,
	};
};

const dispatchProps: DispatchProps = {
	addAllOutgoingListener,
	addExpiredIncomingListener,
	addExpiringIncomingListener,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(Dashboard));
